import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Search,
  Database,
  Sparkles,
  Upload,
  Trash2,
  Edit,
  ChevronDown,
  Globe,
  Building2,
  FileText,
  CheckCircle,
  Clock,
  BarChart3,
  Filter,
  Link,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Industry options
const INDUSTRIES = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'general_contractor', label: 'General Contractor' },
  { value: 'wellness_spa', label: 'Health & Wellness Spa' }
];

// Region options
const REGIONS = [
  { value: 'general', label: 'General (All Regions)' },
  { value: 'alaska', label: 'Alaska' },
  { value: 'pacific_northwest', label: 'Pacific Northwest' },
  { value: 'southwest', label: 'Southwest' },
  { value: 'midwest', label: 'Midwest' },
  { value: 'northeast', label: 'Northeast' },
  { value: 'southeast', label: 'Southeast' },
  { value: 'florida', label: 'Florida' },
  { value: 'texas', label: 'Texas' },
  { value: 'california', label: 'California' }
];

// Category options
const CATEGORIES = [
  { value: 'best_practices', label: 'Best Practices' },
  { value: 'faq', label: 'FAQ' },
  { value: 'regulations', label: 'Regulations & Codes' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'general', label: 'General' }
];

export default function KnowledgeAdmin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Filters
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add/Edit form
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'generate'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    industry: 'plumbing',
    region: 'general',
    category: 'best_practices'
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  
  // Website crawl state
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawlBusinessName, setCrawlBusinessName] = useState('');
  const [crawlMaxPages, setCrawlMaxPages] = useState(15);
  const [crawling, setCrawling] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [crawlPreview, setCrawlPreview] = useState(null);
  const [pendingCrawls, setPendingCrawls] = useState(0);
  const [lastCrawlResult, setLastCrawlResult] = useState(null);

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      const adminCheck = await axios.get(`${BACKEND_URL}/api/admin/check`, {
        withCredentials: true
      });
      
      if (!adminCheck.data.is_admin) {
        toast.error('Admin access required');
        navigate('/client/dashboard');
        return;
      }
      
      await Promise.all([fetchStats(), fetchEntries(), fetchPendingCrawls()]);
    } catch (error) {
      toast.error('Please log in as admin');
      navigate('/client/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/knowledge/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const fetchPendingCrawls = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/knowledge/admin/pending-crawls`, {
        withCredentials: true
      });
      setPendingCrawls(response.data.pending_count);
    } catch (error) {
      console.error('Error fetching pending crawls:', error);
    }
  };

  const handleCrawlWebsite = async () => {
    if (!crawlUrl || !crawlBusinessName) {
      toast.error('Enter website URL and business name');
      return;
    }
    
    setCrawling(true);
    setLastCrawlResult(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/admin/crawl`,
        {
          website_url: crawlUrl,
          business_name: crawlBusinessName,
          industry: formData.industry,
          region: formData.region,
          max_pages: crawlMaxPages
        },
        { withCredentials: true }
      );
      
      const result = response.data;
      setLastCrawlResult(result);
      toast.success(`Crawled ${result.pages_crawled} pages, extracted ${result.knowledge_extracted} entries`);
      setCrawlUrl('');
      setCrawlBusinessName('');
      setCrawlPreview(null);
      fetchEntries();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error crawling website');
    } finally {
      setCrawling(false);
    }
  };

  const handlePreviewCrawl = async () => {
    if (!crawlUrl) {
      toast.error('Enter a website URL first');
      return;
    }
    
    setPreviewing(true);
    setCrawlPreview(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/admin/crawl-preview`,
        { website_url: crawlUrl },
        { withCredentials: true }
      );
      setCrawlPreview(response.data);
      
      // Auto-fill business name from title if empty
      if (!crawlBusinessName && response.data.title) {
        const cleanTitle = response.data.title.split('|')[0].split('-')[0].trim();
        setCrawlBusinessName(cleanTitle);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error previewing website');
    } finally {
      setPreviewing(false);
    }
  };
  
  const handleCrawlPending = async () => {
    setCrawling(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/admin/crawl-pending?limit=10`,
        {},
        { withCredentials: true }
      );
      
      const result = response.data;
      toast.success(`Crawled ${result.crawled} websites, extracted ${result.total_knowledge || 0} entries, found ${result.total_services || 0} services`);
      fetchEntries();
      fetchStats();
      fetchPendingCrawls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error crawling pending websites');
    } finally {
      setCrawling(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterRegion) params.append('region', filterRegion);
      if (filterCategory) params.append('category', filterCategory);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/knowledge/admin/list?${params.toString()}`,
        { withCredentials: true }
      );
      setEntries(response.data.entries);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const handleAddEntry = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/knowledge/admin/add`, formData, {
        withCredentials: true
      });
      toast.success('Knowledge entry added');
      setShowForm(false);
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding entry');
    }
  };

  const handleUpdateEntry = async () => {
    if (!selectedEntry) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/knowledge/admin/${selectedEntry.knowledge_id}`,
        formData,
        { withCredentials: true }
      );
      toast.success('Knowledge entry updated');
      setShowForm(false);
      setSelectedEntry(null);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating entry');
    }
  };

  const handleDeleteEntry = async (knowledgeId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/knowledge/admin/${knowledgeId}`, {
        withCredentials: true
      });
      toast.success('Knowledge entry deleted');
      fetchEntries();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting entry');
    }
  };

  const handleGenerate = async () => {
    if (!formData.industry || !searchQuery) {
      toast.error('Select industry and enter a topic');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/knowledge/admin/generate`,
        {
          industry: formData.industry,
          topic: searchQuery,
          region: formData.region
        },
        { withCredentials: true }
      );
      setGeneratedContent(response.data);
      setFormData({
        ...formData,
        title: response.data.title,
        content: response.data.content,
        category: response.data.category
      });
      setFormMode('add');
      setShowForm(true);
      toast.success('Content generated! Review and save.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error generating content');
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      title: entry.title,
      content: entry.content,
      industry: entry.industry,
      region: entry.region,
      category: entry.category
    });
    setFormMode('edit');
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      industry: 'plumbing',
      region: 'general',
      category: 'best_practices'
    });
    setGeneratedContent(null);
    setFormMode('add');
  };

  useEffect(() => {
    if (!loading) {
      fetchEntries();
    }
  }, [filterIndustry, filterRegion, filterCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div className="flex items-center gap-2">
              <Database className="w-6 h-6 text-sky-400" />
              <h1 className="text-xl font-bold">Knowledge Base</h1>
            </div>
          </div>
          
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-sky-600 hover:bg-sky-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-sky-400" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_entries || 0}</p>
                  <p className="text-sm text-slate-400">Total Entries</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-2xl font-bold">{Object.keys(stats.industries || {}).length}</p>
                  <p className="text-sm text-slate-400">Industries</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold">
                    {new Set(Object.values(stats.industries || {}).flatMap(i => i.regions || [])).size}
                  </p>
                  <p className="text-sm text-slate-400">Regions</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-amber-400" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_usage || 0}</p>
                  <p className="text-sm text-slate-400">Total Queries</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* AI Generation Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold">AI Knowledge Generation</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              {INDUSTRIES.map(ind => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
            
            <select
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              {REGIONS.map(reg => (
                <option key={reg.value} value={reg.value}>{reg.label}</option>
              ))}
            </select>
            
            <Input
              placeholder="Enter topic (e.g., 'frozen pipe repair')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800 border-slate-700"
            />
            
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {generating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate
            </Button>
          </div>
        </div>

        {/* Website Crawling Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Link className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-semibold">Enhanced Website Crawling</h2>
              <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">v2.0</span>
            </div>
            {pendingCrawls > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCrawlPending}
                disabled={crawling}
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              >
                {crawling ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Globe className="w-4 h-4 mr-2" />
                )}
                Crawl {pendingCrawls} Pending
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Website URL (e.g., https://example.com)"
                value={crawlUrl}
                onChange={(e) => { setCrawlUrl(e.target.value); setCrawlPreview(null); }}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <Input
              placeholder="Business name"
              value={crawlBusinessName}
              onChange={(e) => setCrawlBusinessName(e.target.value)}
              className="bg-slate-800 border-slate-700"
            />
            
            <select
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              {INDUSTRIES.map(ind => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
            
            <select
              value={crawlMaxPages}
              onChange={(e) => setCrawlMaxPages(Number(e.target.value))}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value={5}>5 pages</option>
              <option value={10}>10 pages</option>
              <option value={15}>15 pages</option>
              <option value={20}>20 pages (max)</option>
            </select>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handlePreviewCrawl}
                disabled={previewing || !crawlUrl}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 flex-1"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
              <Button
                onClick={handleCrawlWebsite}
                disabled={crawling || !crawlUrl || !crawlBusinessName}
                className="bg-cyan-600 hover:bg-cyan-700 flex-1"
              >
                {crawling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Globe className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* Preview Results */}
          {crawlPreview && (
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700">
              <h4 className="font-medium text-cyan-400 mb-2">Preview: {crawlPreview.title || crawlPreview.url}</h4>
              {crawlPreview.description && (
                <p className="text-sm text-slate-400 mb-3">{crawlPreview.description}</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-500">Headings:</span>
                  <span className="ml-2 font-medium">{crawlPreview.headings_count}</span>
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-500">Links Found:</span>
                  <span className="ml-2 font-medium">{crawlPreview.internal_links_found}</span>
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-500">Content:</span>
                  <span className="ml-2 font-medium">{Math.round(crawlPreview.content_length / 100) / 10}k chars</span>
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-500">Schema:</span>
                  <span className={`ml-2 font-medium ${crawlPreview.has_structured_data ? 'text-green-400' : 'text-slate-500'}`}>
                    {crawlPreview.has_structured_data ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              {crawlPreview.services_preview?.length > 0 && (
                <div className="mt-3">
                  <span className="text-xs text-slate-500">Services detected:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {crawlPreview.services_preview.slice(0, 8).map((svc, i) => (
                      <span key={i} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                        {svc}
                      </span>
                    ))}
                    {crawlPreview.services_preview.length > 8 && (
                      <span className="text-xs text-slate-500">+{crawlPreview.services_preview.length - 8} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Last Crawl Results */}
          {lastCrawlResult && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-emerald-400 mb-2">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Crawl Complete: {lastCrawlResult.business_name}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                <div>
                  <span className="text-slate-400">Pages Crawled:</span>
                  <span className="ml-2 font-bold text-white">{lastCrawlResult.pages_crawled}</span>
                </div>
                <div>
                  <span className="text-slate-400">Knowledge Entries:</span>
                  <span className="ml-2 font-bold text-emerald-400">{lastCrawlResult.knowledge_extracted}</span>
                </div>
                <div>
                  <span className="text-slate-400">Services Found:</span>
                  <span className="ml-2 font-bold text-white">{lastCrawlResult.services_found?.length || 0}</span>
                </div>
                <div>
                  <span className="text-slate-400">FAQs Found:</span>
                  <span className="ml-2 font-bold text-white">{lastCrawlResult.faqs_found || 0}</span>
                </div>
              </div>
              {lastCrawlResult.services_found?.length > 0 && (
                <div>
                  <span className="text-xs text-slate-400">Services extracted:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lastCrawlResult.services_found.slice(0, 12).map((svc, i) => (
                      <span key={i} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                        {svc}
                      </span>
                    ))}
                    {lastCrawlResult.services_found.length > 12 && (
                      <span className="text-xs text-slate-500">+{lastCrawlResult.services_found.length - 12} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <p className="text-xs text-slate-500">
            Enhanced crawler: Parses sitemaps, follows links, extracts structured data (JSON-LD), services, and FAQs.
            Click the search icon to preview before crawling.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Filters:</span>
          </div>
          
          <select
            value={filterIndustry}
            onChange={(e) => setFilterIndustry(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">All Industries</option>
            {INDUSTRIES.map(ind => (
              <option key={ind.value} value={ind.value}>{ind.label}</option>
            ))}
          </select>
          
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">All Regions</option>
            {REGIONS.map(reg => (
              <option key={reg.value} value={reg.value}>{reg.label}</option>
            ))}
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {/* Entries List */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <h2 className="font-semibold">Knowledge Entries ({entries.length})</h2>
          </div>
          
          {entries.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No knowledge entries yet</p>
              <p className="text-sm mt-2">Add entries manually or use AI generation</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {entries.map((entry) => (
                <div
                  key={entry.knowledge_id}
                  className="p-4 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{entry.title}</h3>
                        {entry.is_verified && (
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        )}
                        {entry.pending_review && (
                          <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2 mb-2">
                        {entry.content}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-0.5 bg-sky-500/20 text-sky-400 rounded text-xs">
                          {entry.industry}
                        </span>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                          {entry.region}
                        </span>
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                          {entry.category}
                        </span>
                        {entry.source === 'ai_generated' && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                            AI Generated
                          </span>
                        )}
                        {entry.source === 'contractor_contribution' && (
                          <span className="px-2 py-0.5 bg-pink-500/20 text-pink-400 rounded text-xs">
                            Contractor
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {entry.usage_count || 0} uses
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(entry)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEntry(entry.knowledge_id)}
                        className="text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {formMode === 'edit' ? 'Edit Knowledge Entry' : 'Add Knowledge Entry'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowForm(false); resetForm(); setSelectedEntry(null); }}
              >
                ×
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter a descriptive title"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
                  >
                    {INDUSTRIES.map(ind => (
                      <option key={ind.value} value={ind.value}>{ind.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Region</label>
                  <select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
                  >
                    {REGIONS.map(reg => (
                      <option key={reg.value} value={reg.value}>{reg.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter the knowledge content..."
                  rows={10}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 resize-y"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => { setShowForm(false); resetForm(); setSelectedEntry(null); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={formMode === 'edit' ? handleUpdateEntry : handleAddEntry}
                  className="bg-sky-600 hover:bg-sky-700"
                >
                  {formMode === 'edit' ? 'Update Entry' : 'Add Entry'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
